import React, { useState } from 'react';
import {
  collection, addDoc, deleteDoc, doc, updateDoc, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { K, HOSE as DEFAULT_HOSE, PRECONNECTS, SHORTCUTS, inputStyle } from '../constants';
import { calcFL } from '../utils/formulas';
import { SecHd, Card, MathBox, Lbl, Btn, AddBtn } from '../components/ui';
import { Pencil, X } from 'lucide-react';

const COL_OPTS = [
  { label: 'Red',   value: K.red  },
  { label: 'Blue',  value: K.info },
  { label: 'Gold',  value: K.gold },
  { label: 'Green', value: K.ok   },
  { label: 'Teal',  value: K.teal },
];

// ─── Add / Edit modal ─────────────────────────────────────────────────────────

function PreconnectModal({ initial, onSave, onClose }) {
  const blank = { name: '', hose: '1¾"', len: 200, nozzle: 'Elkhart LP 75', np: 75, gpm: 150, pdp: 125, col: K.red };
  const [form, setForm] = useState(initial || blank);
  const f = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  // A blank NP or GPM would save as 0, which no calculator or the simulator can use.
  const bad = !(form.np > 0) || !(form.gpm > 0);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#000000cc' }} />
      <div className="hc-modal-sheet" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401, background: K.bg1, borderRadius: '16px 16px 0 0', padding: 24, maxWidth: 480, margin: '0 auto', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ width: 40, height: 4, background: K.bdr2, borderRadius: 2, margin: '0 auto 20px' }} />
        <div style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 700, color: K.t0, marginBottom: 20 }}>
          {initial ? 'Edit Preconnect' : 'Add Preconnect'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><Lbl t="Name" /><input value={form.name} onChange={(e) => f('name')(e.target.value)} style={inputStyle()} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <Lbl t="Hose" />
              <select value={form.hose} onChange={(e) => f('hose')(e.target.value)} style={{ ...inputStyle(), cursor: 'pointer' }}>
                {Object.keys(DEFAULT_HOSE).map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div><Lbl t="Length (ft)" /><input type="number" value={form.len} onChange={(e) => f('len')(parseInt(e.target.value) || 0)} style={inputStyle()} /></div>
            <div><Lbl t="Nozzle" /><input value={form.nozzle} onChange={(e) => f('nozzle')(e.target.value)} style={inputStyle()} /></div>
            <div><Lbl t="NP (psi)" /><input type="number" value={form.np} onChange={(e) => f('np')(parseInt(e.target.value) || 0)} style={inputStyle()} /></div>
            <div><Lbl t="GPM" /><input type="number" value={form.gpm} onChange={(e) => f('gpm')(parseInt(e.target.value) || 0)} style={inputStyle()} /></div>
            <div><Lbl t="Standard PDP" /><input type="number" value={form.pdp} onChange={(e) => f('pdp')(parseInt(e.target.value) || 0)} style={inputStyle()} /></div>
            <div>
              <Lbl t="Color" />
              <select value={form.col} onChange={(e) => f('col')(e.target.value)} style={{ ...inputStyle(), cursor: 'pointer' }}>
                {COL_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        {bad && (
          <div style={{ marginTop: 12, fontFamily: K.fSans, fontSize: 12.5, color: K.bad }}>NP and GPM must both be above 0.</div>
        )}
        <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
          <Btn onClick={() => { if (!bad) onSave(form); }} col={K.red}>Save</Btn>
          <button onClick={onClose} style={{ flex: 1, background: 'none', border: `1px solid ${K.bdr}`, color: K.t2, padding: '12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontFamily: K.fSans, fontWeight: 600 }}>Cancel</button>
        </div>
      </div>
    </>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

export function Preconnects({ showMath, preconnects, isAdmin, calcDefaults }) {
  const HOSE = calcDefaults?.hose || DEFAULT_HOSE;
  const [sel, setSel]         = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const safeIdx = Math.min(sel, preconnects.length - 1);
  const pc      = preconnects[safeIdx];
  const loss    = pc && pc.len > 0 ? calcFL(HOSE[pc.hose]?.c ?? 15.5, pc.gpm, pc.len) : 0;

  const handleAdd = async (form) => {
    await addDoc(collection(db, 'preconnects'), { ...form, order: Date.now() });
    setShowModal(false);
  };

  const handleEdit = async (form) => {
    await updateDoc(doc(db, 'preconnects', editing.id), {
      name: form.name, hose: form.hose, len: form.len,
      nozzle: form.nozzle, np: form.np, gpm: form.gpm, pdp: form.pdp, col: form.col,
    });
    setEditing(null);
    setSel(0);
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, 'preconnects', id));
    setSel(0);
  };

  const seedDefaults = async () => {
    const batch = writeBatch(db);
    PRECONNECTS.forEach((p, i) => {
      const ref = doc(collection(db, 'preconnects'));
      batch.set(ref, { name: p.name, hose: p.hose, len: p.len, nozzle: p.nozzle, np: p.np, gpm: p.gpm, pdp: p.pdp, col: p.col, order: i });
    });
    await batch.commit();
  };

  const usingDefaults = !preconnects[0]?.id; // Firestore docs always have an id

  return (
    <div>
      <SecHd title="Preconnect Standards" sub="Fast operational presets first. Math second." />

      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <AddBtn col={K.red} onClick={() => setShowModal(true)}>+ Add Preconnect</AddBtn>
          {usingDefaults && (
            <button onClick={seedDefaults} style={{ background: K.bg2, border: `1px solid ${K.bdr}`, color: K.t2, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontFamily: K.fSans, fontWeight: 600, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
              Seed Defaults
            </button>
          )}
        </div>
      )}

      {preconnects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: K.t3, fontSize: 13 }}>
          No preconnects yet.{isAdmin ? ' Add one above.' : ''}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
            {preconnects.map((p, i) => (
              <button
                key={p.id || p.name}
                onClick={() => setSel(i)}
                style={{ background: sel === i ? `${p.col}22` : K.bg1, border: `1px solid ${sel === i ? p.col : K.bdr}`, borderRadius: 12, padding: '16px 14px', textAlign: 'left', cursor: 'pointer', position: 'relative' }}
              >
                {isAdmin && p.id && (
                  <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditing(p); }}
                      style={{ background: 'none', border: `1px solid ${K.bdr}`, color: K.t3, padding: '4px 6px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    ><Pencil size={14} color={K.t3} strokeWidth={1.75} /></button>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${p.name}"?`)) handleDelete(p.id); }}
                      style={{ background: 'none', border: `1px solid ${K.bdr}`, color: K.t3, padding: '4px 6px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    ><X size={14} color={K.t3} strokeWidth={1.75} /></button>
                  </div>
                )}
                <div style={{ fontSize: 12, color: K.t1, marginBottom: 6, paddingRight: isAdmin && p.id ? 50 : 0 }}>{p.name}</div>
                <div style={{ fontFamily: K.fMono, fontSize: 44, fontWeight: 500, color: p.col, lineHeight: 1 }}>
                  {p.pdp}<span style={{ fontFamily: K.fSans, fontSize: 16, fontWeight: 400, color: K.t2 }}> psi</span>
                </div>
                <div style={{ fontSize: 12, color: K.t2, marginTop: 6 }}>{p.nozzle} · {p.gpm} GPM</div>
              </button>
            ))}
          </div>

          {pc && (
            <>
              <Card accent={pc.col} style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {[
                    { l: 'Standard PDP', v: `${pc.pdp} psi`, col: pc.col, big: true },
                    { l: 'Hose',   v: pc.hose },
                    { l: 'Length', v: pc.len > 0 ? `${pc.len} ft` : 'N/A' },
                    { l: 'Nozzle', v: pc.nozzle },
                    { l: 'NP',     v: `${pc.np} psi` },
                    { l: 'Flow Rate', v: `${pc.gpm} GPM` },
                    ...(pc.len > 0 ? [{ l: 'Calc FL', v: `${loss.toFixed(1)} psi` }] : []),
                  ].map((item, i) => (
                    <div key={`${item.l}-${i}`}>
                      <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t3, letterSpacing: 0.3, marginBottom: 4 }}>{item.l}</div>
                      <div style={{ fontSize: item.big ? 34 : 16, fontWeight: item.big ? 500 : 600, color: item.col || K.t0, fontFamily: item.big ? K.fMono : K.fSans }}>{item.v}</div>
                    </div>
                  ))}
                </div>
              </Card>

              <MathBox show={showMath} steps={pc.len > 0 ? [
                { l: 'FL',         f: `${HOSE[pc.hose]?.c ?? '?'}×(${pc.gpm}/100)²×(${pc.len}/100)`, r: `${loss.toFixed(1)} psi` },
                { l: 'Calc PDP',   f: `${pc.np}+${loss.toFixed(1)}`,                                  r: `${(pc.np + loss).toFixed(1)} psi` },
                { l: 'CMFE Standard', f: 'per apparatus plaque',                                         r: `${pc.pdp} psi` },
              ] : [{ l: 'Deck Gun', f: `Tip NP ${pc.np} psi, standard PDP ${pc.pdp} psi` }]} />
            </>
          )}
        </>
      )}

      <div style={{ marginTop: 24 }}>
        <div style={{ fontFamily: K.fSans, fontSize: 12, fontWeight: 600, color: K.t2, letterSpacing: 0.3, marginBottom: 12 }}>Mental Math Shortcuts</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {SHORTCUTS.map((s) => (
            <div key={s.name} style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderRadius: 8, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontFamily: K.fMono, fontSize: 22, fontWeight: 500, color: K.gold }}>{s.diam}</span>
                <span style={{ fontSize: 11, color: K.t2 }}>{s.name}</span>
              </div>
              <div style={{ fontSize: 11, color: K.t0, marginBottom: 4 }}>{s.rule}</div>
              <div style={{ fontSize: 12, color: K.ok }}>{s.ex}</div>
            </div>
          ))}
        </div>
      </div>

      {showModal && <PreconnectModal onSave={handleAdd} onClose={() => setShowModal(false)} />}
      {editing   && <PreconnectModal initial={editing} onSave={handleEdit} onClose={() => setEditing(null)} />}
    </div>
  );
}
